/**
 * Beitritts-Bildschirm — das Auffangnetz fuer Eingeladene ohne Mitgliedschaft (13.08.2026).
 *
 * Der Schadensfall: eingeladen, per Stack Auth registriert, nie mit `?token=…` angekommen. Das
 * Konto existiert, `tenant_users` ist leer, und damit antwortet jede tenant-gebundene Route 403.
 * Die Oberflaeche zeigte dafuer eine Wand aus Fehlerkarten mit einem "Erneut versuchen"-Knopf,
 * der nie funktionieren konnte — die Einladung lag ja in einer Mail, nicht in der Software.
 *
 * Diese Seite zeigt stattdessen, was tatsaechlich offen ist: `GET /api/invites/offen-fuer-mich`
 * findet ueber die verifizierte JWT-E-Mail die offenen Einladungen (sql/275). Der Beitritt
 * geschieht auf KLICK — kein stiller Auto-Beitritt beim blossen Laden der Seite. Eine
 * Mitgliedschaft ist eine Entscheidung, keine Nebenwirkung eines Seitenaufrufs.
 *
 * Gibt es nichts anzunehmen, steht hier eine Erklaerung, kein Fehler: dass das Konto noch zu
 * keinem Betrieb gehoert, ist kein Defekt der Software.
 *
 * Sonderfall unbestaetigte E-Mail (15.08.2026): seit der fail-closed-Haertung der Einladungs-
 * Annahme (PR #615) liefert `offen-fuer-mich` bei unverifizierter Adresse eine LEERE Liste —
 * Stack Auth erlaubt Konten ohne Bestaetigung, und Antoines echtes Prod-Konto hatte
 * `email_verified: false`. Ohne Hinweis saehe ein legitimer Eingeladener nur "keine
 * Einladungen" und erfuehre nirgends, dass und wo er seine Adresse bestaetigen muss. Deshalb
 * liest die Seite den Verify-Status direkt aus den Stack-Contact-Channels (dasselbe Muster wie
 * mein-konto.tsx) und zeigt statt der Leerlauf-Erklaerung den Bestaetigungs-Hinweis mit
 * Sende-Knopf.
 */
import { useState } from "react";
import { useUser } from "@stackframe/react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Icon, ErrorNote, Button } from "../ui/components";
import { ROLE_LABEL } from "../ui/format";

export interface OffeneEinladung {
  tenantId: string;
  slug: string;
  name: string;
  role: string;
  expiresAt: string;
}

/** Nach dem Beitritt in den Betrieb springen: der Mandant haengt an der Subdomain. */
function inDenBetrieb(slug: string): void {
  const teile = window.location.hostname.split(".");
  if (teile.length < 2 || teile[0] === slug) {
    // Lokale Dev-Hosts ohne Subdomain: derselbe Host, aber jetzt mit Mitgliedschaft — neu laden
    // reicht, damit `/api/me` das Gate durchlaesst.
    window.location.assign("/");
    return;
  }
  teile[0] = slug;
  const port = window.location.port ? `:${window.location.port}` : "";
  window.location.assign(`${window.location.protocol}//${teile.join(".")}${port}/`);
}

function ablauf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}

export function BeitrittsBildschirm({ email }: { email?: string | null }) {
  const user = useUser({ or: "redirect" });
  // Verify-Status direkt aus Stack, wie in mein-konto.tsx: er steht am Contact-Channel. Nur die
  // primaere Adresse zaehlt — an ihr haengt die E-Mail-Bindung der Einladungs-Annahme.
  const emailKanaele = user.useContactChannels().filter((c) => c.type === "email");
  const primaerKanal = emailKanaele.find((c) => c.isPrimary) ?? emailKanaele[0] ?? null;
  const emailUnbestaetigt = primaerKanal != null && !primaerKanal.isVerified;

  const { data, loading, error, reload } = useApi<OffeneEinladung[]>("/api/invites/offen-fuer-mich");
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [verifyGesendet, setVerifyGesendet] = useState(false);
  const [verifyLaeuft, setVerifyLaeuft] = useState(false);
  const [verifyFehler, setVerifyFehler] = useState<string | null>(null);
  const einladungen = data ?? [];

  async function bestaetigungsMailSenden(): Promise<void> {
    if (!primaerKanal) return;
    setVerifyLaeuft(true);
    setVerifyFehler(null);
    try {
      await primaerKanal.sendVerificationEmail();
      setVerifyGesendet(true);
    } catch (e) {
      setVerifyFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setVerifyLaeuft(false);
    }
  }

  async function beitreten(slug: string): Promise<void> {
    setLaeuft(slug);
    setFehler(null);
    try {
      await apiMutate<{ tenantSlug?: string }>("/api/invites/offen-fuer-mich/annehmen", "POST", { slug });
      inDenBetrieb(slug);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Der Beitritt hat nicht geklappt.");
      setLaeuft(null);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12 font-body-md text-on-surface">
      <div className="w-full max-w-lg rounded-xl bg-surface-container-lowest p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
            <Icon name="waving_hand" className="text-[24px]" />
          </div>
          <h1 className="font-headline-sm text-headline-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Willkommen bei MAESTRO
          </h1>
          {email && <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">Angemeldet als {email}</p>}
        </div>

        {loading && !data && (
          <p className="text-center font-body-sm text-body-sm text-on-surface-variant">Einladungen werden geprüft …</p>
        )}

        {/* Ein Ladefehler IST ein Fehler — dafuer bleibt die Fehlerkarte richtig. Das Fehlen einer
            Einladung ist keiner (siehe unten). */}
        {error && <ErrorNote message={error} title="Einladungen konnten nicht geladen werden" onRetry={reload} />}

        {/* Unbestaetigte primaere Adresse: die API blendet Einladungen fail-closed aus — die
            Leerlauf-Erklaerung waere hier eine Irrefuehrung. Stattdessen sagen, was fehlt und
            wo es weitergeht. */}
        {!loading && !error && emailUnbestaetigt && (
          <div className="rounded-xl border border-secondary/25 bg-secondary/[0.07] p-5 text-center">
            <p className="font-body-md text-body-md font-semibold text-secondary dark:text-[#b8ae9e]">
              Ihre E-Mail-Adresse ist noch nicht bestätigt.
            </p>
            <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant">
              Einladungen an {primaerKanal?.value ?? email ?? "diese Adresse"} werden aus Sicherheitsgründen erst
              sichtbar und annehmbar, nachdem Sie die Adresse bestätigt haben. Die Bestätigung finden Sie auch
              unter „Mein Konto“.
            </p>
            {verifyGesendet ? (
              <p className="mt-4 font-body-sm text-body-sm font-semibold text-secondary dark:text-[#b8ae9e]">
                Bestätigungs-Mail gesendet — bitte Postfach prüfen.
              </p>
            ) : (
              <Button
                type="button"
                className="mt-4 !text-[12px]"
                disabled={verifyLaeuft}
                onClick={() => void bestaetigungsMailSenden()}
              >
                <Icon name="mail" className="text-[14px]" /> {verifyLaeuft ? "Sendet …" : "Bestätigungs-Mail senden"}
              </Button>
            )}
            {verifyFehler && (
              <div className="mt-4 text-left">
                <ErrorNote message={verifyFehler} title="Bestätigungs-Mail konnte nicht gesendet werden" />
              </div>
            )}
          </div>
        )}

        {!loading && !error && !emailUnbestaetigt && einladungen.length === 0 && (
          <div className="rounded-xl bg-surface-container p-5 text-center dark:bg-white/[0.04]">
            <p className="font-body-md text-body-md text-on-surface">
              Ihr Konto gehört noch zu keinem Betrieb.
            </p>
            <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant">
              Bitten Sie einen Inhaber des Betriebs um eine Einladung an diese Adresse. Sobald sie
              vorliegt, erscheint sie hier — dann genügt ein Klick.
            </p>
            <Button type="button" variant="secondary" className="mt-4 !text-[12px]" onClick={reload}>
              <Icon name="refresh" className="text-[14px]" /> Erneut nachsehen
            </Button>
          </div>
        )}

        {einladungen.length > 0 && (
          <>
            <p className="mb-3 font-body-sm text-body-sm text-on-surface-variant">
              {einladungen.length === 1
                ? "Für Sie liegt eine Einladung bereit:"
                : `Für Sie liegen ${einladungen.length} Einladungen bereit:`}
            </p>
            <ul className="flex flex-col gap-3">
              {einladungen.map((e) => (
                <li
                  key={e.tenantId}
                  className="flex items-center justify-between gap-4 rounded-xl border border-outline-variant/60 p-4 dark:border-white/10"
                >
                  <div className="min-w-0">
                    <p className="truncate font-body-md text-body-md font-semibold text-on-surface">{e.name}</p>
                    <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant">
                      Rolle: {ROLE_LABEL[e.role] ?? e.role}
                      {ablauf(e.expiresAt) && ` · gültig bis ${ablauf(e.expiresAt)}`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    className="shrink-0 !text-[12px]"
                    disabled={laeuft !== null}
                    onClick={() => void beitreten(e.slug)}
                  >
                    {laeuft === e.slug ? "Tritt bei …" : "Beitreten"}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}

        {fehler && (
          <div className="mt-4">
            <ErrorNote message={fehler} title="Beitritt nicht möglich" />
          </div>
        )}
      </div>
    </div>
  );
}

export default BeitrittsBildschirm;
